import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { motion, AnimatePresence } from "framer-motion";
import { SocialAuthButtons } from "../SocialAuthButtons";

interface EmailPasswordStepProps {
  onNext: (data: { email: string; password: string }) => void;
}

const PASSWORD_REQUIREMENTS = [
  { label: "Mínimo 8 caracteres", test: (pwd: string) => pwd.length >= 8 },
  { label: "Letras e números", test: (pwd: string) => /[a-zA-Z]/.test(pwd) && /\d/.test(pwd) },
];

export const EmailPasswordStep = ({ onNext }: EmailPasswordStepProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Auto-complete inteligente: detectar typos comuns
  const emailSuggestion = useMemo(() => {
    if (!email || email.length < 5) return null;
    
    const emailParts = email.split('@');
    if (emailParts.length !== 2) return null;
    
    const [, domain] = emailParts;
    const typos: Record<string, string> = {
      'gmail.con': 'gmail.com',
      'gmail.co': 'gmail.com',
      'gmai.com': 'gmail.com',
      'hotmail.con': 'hotmail.com',
      'hotmali.com': 'hotmail.com',
      'outlook.con': 'outlook.com',
    };
    
    if (typos[domain]) {
      return email.replace(domain, typos[domain]);
    }
    
    return null;
  }, [email]);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const passwordStrength = PASSWORD_REQUIREMENTS.filter(req => req.test(password)).length;
  const isPasswordValid = passwordStrength === PASSWORD_REQUIREMENTS.length;
  const canSubmit = email && password && acceptedTerms && isEmailValid && isPasswordValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      logger.info('Starting minimal signup', { email });
      
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) throw error;

      if (data.user) {
        logger.info('Signup successful, proceeding to name step', { userId: data.user.id });
        toast({
          title: "Conta criada!",
          description: "Vamos completar seu perfil.",
        });
        onNext({ email, password });
      }
    } catch (error: any) {
      logger.error('Signup failed', error);
      toast({
        variant: "destructive",
        title: "Erro ao criar conta",
        description: error.message === "User already registered" 
          ? "Este email já está cadastrado. Tente fazer login."
          : "Erro ao criar conta. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <h1 className="text-3xl font-bold">Criar Conta</h1>
        <p className="text-muted-foreground">
          Junte-se a 50.000+ corredores
        </p>
      </motion.div>

      {/* Social Auth Buttons */}
      <SocialAuthButtons mode="signup" />

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground transition-colors" />
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              className="pl-9 pr-10 transition-all duration-200"
              aria-invalid={email.length > 0 && !isEmailValid}
              disabled={loading}
              autoComplete="email"
            />
            <AnimatePresence>
              {emailTouched && isEmailValid && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <CheckCircle2 className="w-4 h-4 text-success" />
                </motion.div>
              )}
              {emailTouched && !isEmailValid && email && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <AlertCircle className="w-4 h-4 text-destructive" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Sugestão de email inteligente */}
          <AnimatePresence>
            {emailSuggestion && (
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                type="button"
                onClick={() => setEmail(emailSuggestion)}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Você quis dizer <strong className="text-primary">{emailSuggestion}</strong>?
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Password */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
          <Label htmlFor="password">Senha</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground transition-colors" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-9 pr-10 transition-all duration-200"
              disabled={loading}
              autoComplete="new-password"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1 h-8 w-8 hover:bg-transparent transition-colors"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Password Strength */}
          <AnimatePresence>
            {password && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2 overflow-hidden mt-3"
              >
                <div className="flex gap-1">
                  {PASSWORD_REQUIREMENTS.map((_, index) => (
                    <motion.div
                      key={index}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: index < passwordStrength ? 1 : 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`h-1 flex-1 rounded-full origin-left transition-colors ${
                        index < passwordStrength
                          ? "bg-primary"
                          : "bg-muted"
                      }`}
                    />
                  ))}
                </div>
                <div className="space-y-1">
                  {PASSWORD_REQUIREMENTS.map((req, index) => {
                    const isPassed = req.test(password);
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center gap-2 text-xs"
                      >
                        {isPassed ? (
                          <CheckCircle2 className="w-3 h-3 text-success" />
                        ) : (
                          <AlertCircle className="w-3 h-3 text-muted-foreground" />
                        )}
                        <span
                          className={
                            isPassed
                              ? "text-success"
                              : "text-muted-foreground"
                          }
                        >
                          {req.label}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Terms */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all duration-200 ${
            acceptedTerms
              ? "border-primary/50 bg-primary/5"
              : "border-border hover:border-primary/30 hover:bg-accent/50"
          }`}
        >
          <Checkbox
            id="terms"
            checked={acceptedTerms}
            onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
            disabled={loading}
            className="mt-0.5"
          />
          <label
            htmlFor="terms"
            className="text-sm font-medium leading-tight cursor-pointer flex-1"
          >
            Li e concordo com os{" "}
            <a href="/terms" className="text-primary hover:underline font-semibold transition-colors">
              Termos de Uso
            </a>{" "}
            e{" "}
            <a href="/privacy" className="text-primary hover:underline font-semibold transition-colors">
              Política de Privacidade
            </a>
          </label>
        </motion.div>

        {/* Submit */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            type="submit"
            className="w-full transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            disabled={!canSubmit || loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                />
                Criando conta...
              </span>
            ) : (
              "Continuar"
            )}
          </Button>
        </motion.div>
      </form>

      {/* Login Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center text-sm"
      >
        <span className="text-muted-foreground">Já tem uma conta? </span>
        <Button
          variant="link"
          className="p-0 h-auto hover:underline"
          onClick={() => navigate("/login")}
          disabled={loading}
        >
          Fazer login
        </Button>
      </motion.div>
    </div>
  );
};